import {
  Component,
  OnInit,
  OnDestroy,
  QueryList,
  ViewChildren,
  ViewChild,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
  CdkDrag,
  CDK_DRAG_CONFIG,
  DragDropConfig,
} from '@angular/cdk/drag-drop';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NgChartsModule } from 'ng2-charts';

import { CostBreakdownComponent } from './cost-breakdown-component/cost-breakdown.component';
import {
  RawMaterial,
  LabourItem,
  IndirectLabourItem,
  FixedItem,
  VariableOverhead,
  RawMaterialsComponent,
  Competitor,
  CombinedCostItem,
} from './raw-materials-component/raw-materials.component';
import { DonutChartComponent } from './donut-chart-component/donut-chart.component';
import { AivorInsightsComponent } from './aivor-insights/aivor-insights.component';
import { MobileCalcViewComponent } from './mobile-calc-view/mobile-calc-view.component';
import { CostItemEditData } from './mobile-calc-view/cost-item-offcanvas/cost-item-offcanvas.component';
import { environment } from '../../../../environments/environment';
import {
  catchError,
  EMPTY,
  map,
  Observable,
  switchMap,
  throwError,
} from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanDeactivate,
  GuardResult,
  MaybeAsync,
  RouterStateSnapshot,
} from '@angular/router';
import { AutoSaveService } from './auto-save-service';
import { ToastService } from '../../shared/services/toast.service';

type WidgetKey =
  | 'rawMaterials'
  | 'aivorInsights'
  | 'donutChart'
  | 'costBreakdown';

@Component({
  selector: 'app-cost-calculator',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    NgChartsModule,
    DonutChartComponent,
    CostBreakdownComponent,
    RawMaterialsComponent,
    AivorInsightsComponent,
    MobileCalcViewComponent,
  ],
  templateUrl: './cost-calculator.component.html',
  styleUrls: ['./cost-calculator.component.css'],
  providers: [
    {
      provide: CDK_DRAG_CONFIG,
      useValue: {
        dragStartThreshold: 0,
        pointerDirectionChangeThreshold: 0,
        zIndex: 1000,
        autoScrollStep: 0,
        dragStartsAfter: 150,
      } as DragDropConfig,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CostCalculatorComponent
  implements OnInit, OnDestroy, CanDeactivate<CostCalculatorComponent>
{
  @ViewChild('rawMaterialsComponent') rawMaterialsComponent: any;
  
  productName = '';
  products: any[] = []; // Products for mobile dropdown
  private inactivityTimer: any;
  private isMouseInside = false;
  suggestedSellingPrice: number = 0;
  AIcalculatedMarkupPrice: number = 0;
  calculatorSellingPrice = 0;
  totalCost = 0;
  markupPercent: number = 0;

  rawMaterials: RawMaterial[] = [];

  screenWidth: number = window.innerWidth;
  directMaterials: RawMaterial[] = [...this.rawMaterials];
  directLabor: LabourItem[] = [];
  indirectLabor: IndirectLabourItem[] = [];
  fixedOverheads: FixedItem[] = [];
  variableOverheads: VariableOverhead[] = [];
  competitors: Competitor[] = [];
  selectedProductName: string = '';
  tenantProfile: any = null;
  suggestedPrice = 0;
  markupPrice = 0;
  recommendations: string[] = [];

  widgets: WidgetKey[] = [
    'rawMaterials',
    'aivorInsights',
    'donutChart',
    'costBreakdown',
  ];

  private dragDelay = 500;
  private dragTimeout: any;
  private isDragging = false;

  @ViewChildren(CdkDrag) dragItems!: QueryList<CdkDrag>;

  /** 1) Map of productName → CombinedCostItem[] */
  public allProductCosts: Record<string, CombinedCostItem[]> = {};
  tenantName: any;
  fullName: any;
  accountType = 'full';
  
  // Mobile view properties
  mobileSelectedProductId: number | null = null;
  combinedCostItems: CombinedCostItem[] = [];
  sellingPrice = 0;
  aiSuggestedPrice = 0;
  totalCostWithProfit = 0;
  profitMargin = 0;
  profitMarginPercentage = 0;
  isLoading = false;

  mobileView: 'calculator' | 'summary' | 'graphs' | 'aivor' = 'calculator';
  mobileProductSellingPrice = 0;

  mobileBusy = false;
  mobileBusyLabel = '';

  private showMobileToast(type: 'success' | 'error', message: string): void {
    this.toast.show(type, message, 2500);
  }

  onAllProductCostsLoaded(costMap: Record<string, CombinedCostItem[]>): CombinedCostItem[] {
    console.log('Received all product costs from child:', costMap);

    // Start from existing map so we don't lose persisted sellingPrice entries
    const validatedCostMap: Record<string, CombinedCostItem[]> = {
      ...this.allProductCosts,
    };

    const combinedCosts: CombinedCostItem[] = [];

    Object.entries(costMap).forEach(([productName, costs]) => {
      const hasSellingPrice = costs?.some(
        (c) => typeof c.sellingPrice === 'number'
      );

      const existing = validatedCostMap[productName];
      const existingHasSellingPrice = existing?.some(
        (c) => typeof c.sellingPrice === 'number'
      );

      // First calculate the total cost (for current selections)
      this.totalCost = this.getTotalCost();

      // Skip invalid entries only if no selling price is present
      if (!hasSellingPrice && this.totalCost <= 0) {
        return;
      }

      // Filter out invalid entries
      if (this.isValidProduct(productName, costs)) {
        // If the new data carries a selling price or we don't have one yet, replace it.
        // Otherwise, keep the existing entry so we don't lose the stored selling price.
        if (hasSellingPrice || !existingHasSellingPrice) {
          validatedCostMap[productName] = costs;
        }
      } else {
        console.warn('Filtered out invalid product:', productName, costs);
      }
    });

    Object.entries(costMap).forEach(([productName, costs]) => {
      const combined = this.getCombinedCosts(productName, costs);
      combinedCosts.push(...combined);
    });

    // Reassign to a fresh object so children see the change
    this.allProductCosts = { ...validatedCostMap };

    return combinedCosts;
  }
  private isValidProduct(
    productName: string,
    costs: CombinedCostItem[]
  ): boolean {
    // Check if product name is valid (not just a random number)
    if (!productName || productName.trim() === '') {
      return false;
    }

    // Check if it's just a random number (adjust pattern as needed)
    if (/^\d+$/.test(productName) && productName.length > 3) {
      return false;
    }

    // Check if costs array is valid and not empty
    if (!costs || !Array.isArray(costs) || costs.length === 0) {
      return false;
    }

    return true;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private autoSaveService: AutoSaveService,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {}

  async canDeactivate(): Promise<boolean> {
    return await this.autoSaveService.checkAllComponentsForUnsavedChanges();
  }

  ngOnInit(): void {
    const JWT = localStorage.getItem('authToken');
    const decodedToken: any = jwtDecode(JWT!);
    this.tenantName = decodedToken.TenantName;
    this.fullName = decodedToken.FullName;
    this.accountType = decodedToken.AccountType || 'full';
    this.loadDashboardLayout();
    this.fetchLatestTenantProfile();
    this.onResize();

    this.route.queryParams.subscribe((params) => {
      const view = params['view'];
      if (view === 'summary' || view === 'graphs' || view === 'aivor') {
        this.mobileView = view;
      } else {
        this.mobileView = 'calculator';
      }
    });
  }

  @HostListener('window:resize', [])
  onResize() {
    this.screenWidth = window.innerWidth;
  }

  ngOnDestroy(): void {
    this.clearDrag();
  }

  private loadDashboardLayout(): void {
    const saved = localStorage.getItem('dashboardLayout');
    if (saved) {
      try {
        const layout = JSON.parse(saved) as WidgetKey[];
        this.widgets = layout.filter((w) =>
          [
            'rawMaterials',
            'aivorInsights',
            'donutChart',
            'costBreakdown',
          ].includes(w)
        );
      } catch {
        localStorage.removeItem('dashboardLayout');
      }
    }
  }

  dropListDropped(event: CdkDragDrop<WidgetKey[]>): void {
    moveItemInArray(this.widgets, event.previousIndex, event.currentIndex);
    localStorage.setItem('dashboardLayout', JSON.stringify(this.widgets));
  }

  resetToDefaultLayout(): void {
    this.widgets = [
      'rawMaterials',
      'aivorInsights',
      'donutChart',
      'costBreakdown',
    ];
    localStorage.setItem('dashboardLayout', JSON.stringify(this.widgets));
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    this.isMouseInside = false;
    clearTimeout(this.inactivityTimer);
  }

  startInactivityTimer(event: MouseEvent) {
    this.isMouseInside = true;
    this.resetInactivityTimer(event);
  }

  resetInactivityTimer(event?: MouseEvent) {
    if (event) this.isMouseInside = true;
    clearTimeout(this.inactivityTimer);
    if (this.isMouseInside) {
      this.inactivityTimer = setTimeout(() => {
        this.isMouseInside = false;
      }, 2000);
    }
  }

  startDrag(drag: CdkDrag, event: MouseEvent): void {
    if (event.button !== 0 || this.isDragging) return;
    event.preventDefault();
    const el = drag.element.nativeElement;
    el.classList.add('drag-delay-active');
    this.dragTimeout = setTimeout(() => {
      this.isDragging = true;
      el.classList.add('drag-active');
      el.dispatchEvent(
        new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          clientX: event.clientX,
          clientY: event.clientY,
        })
      );
      el.classList.remove('drag-delay-active');
      document.addEventListener('mouseup', this.clearDrag.bind(this));
    }, this.dragDelay);
  }

  private clearDrag(): void {
    clearTimeout(this.dragTimeout);
    this.isDragging = false;
    this.dragItems.forEach((d) => {
      const el = d.element.nativeElement;
      el.classList.remove('drag-delay-active', 'drag-active');
    });
    document.removeEventListener('mouseup', this.clearDrag.bind(this));
  }

  onProductNameChange(name: string): void {
    this.productName = name;
  }

  onSelectedProductChange(event: { selectedProductName: string }) {
  }

  handleAiSuggestionLoaded(event: {
    aiSuggestedSellingPrice: number;
    aiSuggestedMarkup: number;
  }) {
    this.suggestedSellingPrice = event.aiSuggestedSellingPrice ?? 0;
    this.AIcalculatedMarkupPrice = event.aiSuggestedMarkup ?? 0;
  }

  /**
   * Retrieves the ID of the tenant profile with the highest `tenantProfileID` value.
   * This is done by fetching all tenant profiles and then reducing the array
   * to find the profile with the maximum ID.
   *
   * @returns An Observable that emits the latest `tenantProfileID` (number) or `null` if no profiles are found or an error occurs.
   */
  getLatestTenantProfileId(): Observable<number | null> {
    const token =
      localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

    if (!token) {
      console.error('Authentication error: No auth token found.');
      return throwError(
        () => new Error('Authentication error: No auth token found.')
      );
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    // Endpoint to get all tenant profiles
    const tenantProfilesUrl = `${environment.apiUrl}/api/TenantProfile/GetAll`;

    return this.http.get<any[]>(tenantProfilesUrl, { headers }).pipe(
      map((data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          // Find the tenantProfileID with the highest value
          const latestProfile = data.reduce((prev, current) =>
            prev.tenantProfileID > current.tenantProfileID ? prev : current
          );
          return latestProfile.tenantProfileID;
        } else {
          console.warn('No tenant profiles found or data is empty.');
          return null;
        }
      }),
      catchError((error) => {
        console.error('Error fetching tenant profiles:', error);
        return throwError(() => new Error('Failed to fetch tenant profiles.'));
      })
    );
  }

  /**
   * Fetches the latest tenant profile by first getting the latest ID,
   * then using that ID to retrieve the full profile.
   */
  fetchLatestTenantProfile(): void {
    // First, get the latest tenant profile ID
    this.getLatestTenantProfileId()
      .pipe(
        // Use switchMap to switch to a new observable (the profile fetch)
        // once the latest ID is available.
        switchMap((latestTenantId) => {
          if (latestTenantId !== null) {
            const token =
              localStorage.getItem('authToken') ||
              sessionStorage.getItem('authToken');
            const headers = new HttpHeaders({
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            });
            // Construct the URL with the fetched latestTenantId
            const profileUrl = `${environment.apiUrl}/api/TenantProfile/GetById?TenantProfileId=${latestTenantId}`;
            return this.http.get<any>(profileUrl, { headers });
          } else {
            // If no latestTenantId is found, complete the stream without making a request
            console.warn(
              'Cannot fetch tenant profile: No latest tenant ID found.'
            );
            return EMPTY; // Use EMPTY to complete the observable stream
          }
        }),
        catchError((error) => {
          console.error('Error fetching the latest tenant profile:', error);
          // Re-throw the error to be handled by the subscriber if needed
          return throwError(
            () => new Error('Failed to fetch the latest tenant profile.')
          );
        })
      )
      .subscribe({
        next: (profileData) => {
          if (profileData) {
            this.tenantProfile = profileData;
          } else {
          }
        },
        error: (err) => {
          console.error('Subscription error for latest tenant profile:', err);
        },
        complete: () => {
        },
      });
  }

  onmarkupPercentChange(data: { markupPercent: number }) {
    this.markupPercent = data.markupPercent;
    this.calculatorSellingPrice = this.getSellingPrice();
    this.getMarkupPrice();
    this.cdr.detectChanges();
  }

  onCostDataChange(data: {
    directMaterials: RawMaterial[];
    directLabor: LabourItem[];
    indirectLabor: IndirectLabourItem[];
    fixedOverheads: FixedItem[];
    variableOverheads: VariableOverhead[];
    competitors: Competitor[];
  }): void {
    console.log('onCostDataChange called with:', {
      materialsCount: data.directMaterials.length,
      laborCount: data.directLabor.length,
      indirectCount: data.indirectLabor.length,
      fixedCount: data.fixedOverheads.length,
      variableCount: data.variableOverheads.length
    });
    
    this.rawMaterials = [...data.directMaterials];
    this.directMaterials = [...data.directMaterials];
    this.directLabor = [...data.directLabor];
    this.indirectLabor = [...data.indirectLabor];
    this.fixedOverheads = [...data.fixedOverheads];
    this.variableOverheads = [...data.variableOverheads];
    this.competitors = [...data.competitors];
    this.totalCost = this.getTotalCost();
    this.calculatorSellingPrice = this.getSellingPrice();
    this.getMarkupPrice();
    
    // Update mobile properties
    this.updateMobileProperties();
  }

  getMarkupPrice(): number {
    return this.getSellingPrice() - this.getTotalCost();
  }

  updateMobileProperties(): void {
    console.log('updateMobileProperties called:', {
      totalCost: this.totalCost,
      sellingPrice: this.sellingPrice,
      profitMargin: this.profitMargin,
      profitMarginPercentage: this.profitMarginPercentage,
      selectedProductName: this.selectedProductName
    });
    
    this.sellingPrice = this.getSellingPrice();
    this.aiSuggestedPrice = this.getAISuggestedSellingPrice();
    this.totalCostWithProfit = this.totalCost + this.profitMargin;

    // On mobile we treat profitMarginPercentage as the user-controlled markup percent.
    // Do not overwrite it with a derived value, otherwise the knob resets to 0 on reload.
    if (this.rawMaterialsComponent?.selectedProductId) {
      this.mobileSelectedProductId = this.rawMaterialsComponent.selectedProductId;
      const prod = this.rawMaterialsComponent.products?.find(
        (p: any) => p.itemID === this.rawMaterialsComponent.selectedProductId
      );
      const saved = prod?.profitMarginPercent;
      if (typeof saved === 'number' && !isNaN(saved)) {
        this.profitMarginPercentage = saved;
        this.markupPercent = saved;
      }
    }
    
    this.mobileProductSellingPrice = this.getSelectedProductSellingPrice();

    // Create combined cost items for mobile view
    this.combinedCostItems = this.getCombinedCosts(this.selectedProductName,
      this.allProductCosts[this.selectedProductName] || []);
    
    // Force change detection to update mobile view
    this.cdr.detectChanges();
    
    console.log('updateMobileProperties finished:', {
      totalCost: this.totalCost,
      sellingPrice: this.sellingPrice,
      profitMarginPercentage: this.profitMarginPercentage
    });
  }

  getSellingPrice(): number {
    const sellingPrice = (1 + this.markupPercent / 100) * this.getTotalCost();
    return Math.round(sellingPrice * 100) / 100;
  }

  get isVatVendor(): boolean {
    return this.tenantProfile?.vendorType === 'Vat Vendor';
  }
  getAISuggestedSellingPrice(): number {
    if (this.suggestedSellingPrice <= 0) return 0;
    return this.suggestedSellingPrice - this.getTotalCost();
  }

  getTotalCost(): number {
    const materialTotal = this.directMaterials.reduce(
      (sum, rm) => sum + (rm.unitCost ?? 0),
      0
    );
    const directLabourTotal = this.directLabor.reduce(
      (sum, li) => sum + (li.unitCost ?? 0),
      0
    );
    const indirectLabourTotal = this.indirectLabor.reduce(
      (sum, li) => sum + (li.unitCost ?? 0),
      0
    );
    const fixedTotal = this.fixedOverheads.reduce(
      (sum, fi) => sum + (fi.unitCost ?? 0),
      0
    );
    const variableTotal = this.variableOverheads.reduce(
      (sum, v) => sum + (v.unitCost ?? 0),
      0
    );

    return (
      materialTotal +
      directLabourTotal +
      indirectLabourTotal +
      fixedTotal +
      variableTotal
    );
  }

  /** Returns the selling price for the currently selected product (from Items table) */
  getSelectedProductSellingPrice(): number {
    // Prefer the live products array so updates from product edits are reflected immediately
    if (this.rawMaterialsComponent?.selectedProductId) {
      const prod = this.rawMaterialsComponent.products?.find(
        (p: any) => p.itemID === this.rawMaterialsComponent.selectedProductId
      );
      if (typeof prod?.sellingPrice === 'number') return prod.sellingPrice;
    }
    if (!this.productName) return 0;
    const costs = this.allProductCosts[this.productName];
    if (!costs || costs.length === 0) return 0;
    const found = costs.find((c) => typeof c.sellingPrice === 'number');
    return found?.sellingPrice ?? 0;
  }

  public mapToKeys(
    obj: Record<string, CombinedCostItem[]> | null
  ): Array<{ key: string; value: CombinedCostItem[] }> {
    if (!obj) return [];
    return Object.entries(obj).map(([k, v]) => ({ key: k, value: v }));
  }

  getCombinedCosts(productName: string, costs: CombinedCostItem[]): CombinedCostItem[] {
    const productId = (this as any).selectedProductId ?? 0;

    const materials: CombinedCostItem[] = costs.filter((c) => c.category === 'Raw Material').map((m) => ({
      productId,
      productName,
      category: 'Raw Material',
      itemOrRole: m.itemOrRole,
      unitCost: m.unitCost,
    }));

    const directLabors: CombinedCostItem[] = costs.filter((c) => c.category === 'Direct Labor').map((l) => ({
      productId,
      productName,
      category: 'Direct Labor',
      itemOrRole: l.itemOrRole,
      unitCost: l.unitCost,
    }));

    const indirectLabors: CombinedCostItem[] = costs.filter((c) => c.category === 'Indirect Labor').map((l) => ({
      productId,
      productName,
      category: 'Indirect Labor',
      itemOrRole: l.itemOrRole,
      unitCost: l.unitCost,
    }));

    const fixed: CombinedCostItem[] = costs.filter((c) => c.category === 'Fixed Overhead').map((f) => ({
      productId,
      productName,
      category: 'Fixed Overhead',
      itemOrRole: f.itemOrRole,
      unitCost: f.unitCost,
    }));

    const variable: CombinedCostItem[] = costs.filter((c) => c.category === 'Variable Overhead').map((v) => ({
      productId,
      productName,
      category: 'Variable Overhead',
      itemOrRole: v.itemOrRole,
      unitCost: v.unitCost ?? 0,
    }));

    return [
      ...materials,
      ...directLabors,
      ...indirectLabors,
      ...fixed,
      ...variable,
    ];
  }

  public getProductTotal(costs: CombinedCostItem[]): number {
    return costs.reduce((sum, c) => sum + c.unitCost, 0);
  }

  colSpanFor(widget: WidgetKey): string {
    const isSmallScreen = true;
    switch (widget) {
      case 'rawMaterials':
        return isSmallScreen ? 'col-12' : 'col-md-8';
      case 'aivorInsights':
        return 'col-md-4';
      default:
        return 'col-md-4';
    }
  }

  expandedProducts: { [key: string]: boolean } = {};

  toggleExpand(productKey: string): void {
    this.expandedProducts[productKey] = !this.expandedProducts[productKey];
  }

  // Mobile event handlers
  onMobileAddProduct(): void {
    // The mobile-calc-view opens the offcanvas directly; this hook is available for future use.
  }

  private pendingMobileProductId: number | null = null;

  onMobileProductChange(productId: number): void {
    this.mobileSelectedProductId = productId;
    if (!this.rawMaterialsComponent) {
      return;
    }
    this.pendingMobileProductId = productId;
    this.applyPendingProduct(0);
  }

  private applyPendingProduct(attempt: number): void {
    if (this.pendingMobileProductId === null) return;
    if (!this.rawMaterialsComponent) return;

    const productId = this.pendingMobileProductId;

    const product = this.rawMaterialsComponent.products?.find(
      (p: any) => p.itemID === +productId
    );

    if (product) {
      this.pendingMobileProductId = null;
      this.rawMaterialsComponent.selectProduct(product);
      this.mobileProductSellingPrice = product.sellingPrice ?? 0;
      const saved = product.profitMarginPercent;
      if (typeof saved === 'number' && !isNaN(saved)) {
        this.profitMarginPercentage = saved;
        this.markupPercent = saved;
        this.rawMaterialsComponent.markupPercent = saved;
        if (typeof this.rawMaterialsComponent.onMarkupPercentChange === 'function') {
          this.rawMaterialsComponent.onMarkupPercentChange(true);
        }
      }
      setTimeout(() => {
        this.mobileBusy = true;
        this.mobileBusyLabel = 'Loading product…';
        setTimeout(() => {
          this.mobileBusy = false;
          this.mobileBusyLabel = '';
        }, 600);
      }, 50);
    } else if (attempt < 10) {
      setTimeout(() => this.applyPendingProduct(attempt + 1), 200);
    }
  }

  onMobileTargetCostChange(value: number | null): void {
    if (this.rawMaterialsComponent && typeof this.rawMaterialsComponent.onTargetCostChange === 'function') {
      this.rawMaterialsComponent.onTargetCostChange(value);
    }
  }

  onMobileMarginChange(marginPercentage: number): void {
    console.log('Mobile margin change:', marginPercentage);
    // Update margin percentage and trigger recalculation
    this.profitMarginPercentage = marginPercentage;
    this.markupPercent = marginPercentage;

    if (this.rawMaterialsComponent) {
      this.rawMaterialsComponent.markupPercent = marginPercentage;
      if (typeof this.rawMaterialsComponent.onMarkupPercentChange === 'function') {
        this.rawMaterialsComponent.onMarkupPercentChange();
      }
    }
    this.onmarkupPercentChange({ markupPercent: marginPercentage });
  }

  onMobileFOHSaveAll(): void {
    // The FOH popup uses the same full mobile save (promotes placeholders, saves
    // via saveChanges, shows the toast). onMobileSaveAll clears the pending flag.
    void this.onMobileSaveAll();
  }

  async onMobileSaveAll(): Promise<void> {
    try {
      if (!this.rawMaterialsComponent) {
        console.warn('Mobile Save All: rawMaterialsComponent not available');
        return;
      }

      const productId = this.rawMaterialsComponent.selectedProductId;
      if (!productId) {
        console.warn('Mobile Save All: no selected product id');
        this.showMobileToast('error', 'Cannot save: Product not selected.');
        return;
      }

      this.mobileBusy = true;
      this.mobileBusyLabel = 'Saving…';

      // Ensure the markupPercent used by RawMaterialsComponent is synced before saving
      if (this.rawMaterialsComponent) {
        this.rawMaterialsComponent.markupPercent = this.profitMarginPercentage;
        if (typeof this.rawMaterialsComponent.onMarkupPercentChange === 'function') {
          this.rawMaterialsComponent.onMarkupPercentChange();
        }
      }

      // Attach every visible Fixed Overhead row to the product (same one-click
      // behaviour as desktop "Save All") before persisting.
      if (typeof this.rawMaterialsComponent.promoteAllFixedPlaceholders === 'function') {
        this.rawMaterialsComponent.promoteAllFixedPlaceholders();
      }

      // saveChanges() reports failure through its return value rather than throwing, so the
      // catch below never sees a failed save.
      const saved = await this.rawMaterialsComponent.saveChanges(productId, 'manual');
      if (!saved) {
        this.showMobileToast('error', this.rawMaterialsComponent.competitorSaveError || 'Save failed. Please try again.');
        return;
      }

      // Dismiss the FOH "changes not yet saved" popup once persisted.
      this.rawMaterialsComponent.fohBatchChangePending = false;

      this.showMobileToast('success', 'Saved successfully');
    } catch (err) {
      console.error('Mobile Save All failed:', err);
      this.showMobileToast('error', 'Save failed. Please try again.');
    } finally {
      this.mobileBusy = false;
      this.mobileBusyLabel = '';
    }
  }

  onMobileSellingPriceChange(newPrice: number): void {
    if (this.competitors && this.competitors.length > 0) {
      this.competitors[0].price = newPrice;
    }
  }

  onMobileCostItemDelete(data: CostItemEditData): void {
    if (!this.rawMaterialsComponent) {
      console.warn('Mobile cost item delete: rawMaterialsComponent not available');
      return;
    }
    if (!this.rawMaterialsComponent.selectedProductId) {
      this.showMobileToast('error', 'Select a product first.');
      return;
    }
    try {
      if (typeof this.rawMaterialsComponent.deleteFromMobile === 'function') {
        this.rawMaterialsComponent.deleteFromMobile(data);
        this.showMobileToast('success', 'Item removed');
      }
    } catch (err) {
      console.error('Mobile cost item delete failed:', err);
      this.showMobileToast('error', 'Could not delete item.');
    }
  }

  onMobileCostItemUpsert(data: CostItemEditData): void {
    if (!this.rawMaterialsComponent) {
      console.warn('Mobile cost item upsert: rawMaterialsComponent not available');
      return;
    }

    if (data.category === 'Product') {
      const name = data.itemName?.trim();
      const price = data.sellingPrice;
      if (!name || (price != null && price < 0)) {
        this.showMobileToast('error', 'Enter a product name.');
        return;
      }
      this.rawMaterialsComponent.createProductFromMobile(name, price ?? 0)
        .then(() => this.showMobileToast('success', 'Product created'))
        .catch(() => this.showMobileToast('error', 'Could not create product.'));
      return;
    }

    if (!this.rawMaterialsComponent.selectedProductId) {
      this.showMobileToast('error', 'Select a product first.');
      return;
    }

    try {
      if (typeof this.rawMaterialsComponent.upsertFromMobile === 'function') {
        this.rawMaterialsComponent.upsertFromMobile(data);
        if (data.category === 'Variable Overhead' || data.category === 'Competitor') {
          void this.onMobileSaveAll();
        } else {
          this.showMobileToast('success', 'Item saved');
        }
      } else {
        console.warn('Mobile cost item upsert: upsertFromMobile not implemented on RawMaterialsComponent');
      }
    } catch (err) {
      console.error('Mobile cost item upsert failed:', err);
      this.showMobileToast('error', 'Could not apply changes.');
    }
  }
}
